<template>
  <div class="card main-chart p-3" :class="{ 'main-chart--embedded': injectedReportData }" :style="!injectedReportData ? 'height: 500px;' : ''">
    <div class="d-flex justify-content-between align-items-center mb-3" v-if="!hideControls">
      <ControlChart v-if="reportData.type_chart !== 'retention'" />
      <div v-else></div>
      <div class="d-flex align-items-center gap-2">
        <CompareControl v-if="!injectedReportData" />
        <DateRange compact />
      </div>
    </div>
    <!-- Independent of hideControls: the standalone report-view page hides this toolbar's own
         date picker (it has its own PeriodGranularitySelector) but still needs the warning.
         Only real exclusion is dashboard widgets, which inject a local, isolated report state
         and always have their dates overridden by the dashboard's global period anyway. -->
    <HardcodedDateWarning v-if="!injectedReportData" />

    <!-- Empty state: data loaded but all values are zero -->
    <div v-if="!loading && chartData && isAllZeroData" class="chart-empty-state">
      <i class="fas fa-chart-line chart-empty-icon"></i>
      <span class="chart-empty-text">{{ $t('ReportBuilder.chart.noData') }}</span>
    </div>

    <template v-else>
      <!-- Line, Area, Line Cumulative, Retention -->
      <div v-if="isLineType">
        <CLine
          v-if="!loading && chartData"
          :options="chartOptions"
          :data="chartData"
          :style="chartStyles"
        />
      </div>

      <!-- Bar, Stacked Bar -->
      <div v-else-if="isBarType">
        <Bar
          v-if="!loading && chartData"
          :options="chartOptions"
          :data="chartData"
          :style="chartStyles"
        />
      </div>

      <!-- Horizontal Bar -->
      <div v-else-if="isHorizontalBarType">
        <Bar
          v-if="!loading && chartData"
          :options="chartOptions"
          :data="chartData"
          :style="chartStyles"
        />
      </div>

      <!-- Pie -->
      <div v-else-if="isPieType">
        <Pie
          v-if="!loading && chartData"
          :options="chartOptions"
          :data="pieChartData"
          :plugins="pieChartPlugins"
          :style="chartStyles"
        />
      </div>

      <!-- Single number (KPI) -->
      <div v-else-if="isNumberType">
        <div v-if="!loading && chartData" class="kpi-wrap">
          <div v-for="(label, i) in chartData.labels" :key="i" class="kpi-tile">
            <div class="kpi-value">{{ formatKpiValue(chartData.datasets[0].data[i]) }}</div>
            <div class="kpi-label">{{ label }}</div>
            <!-- Сравнение с другим периодом: дельта прямо в плитке -->
            <div
              v-if="kpiDelta(label, chartData.datasets[0].data[i])"
              class="kpi-delta"
              :class="'kpi-delta--' + kpiDelta(label, chartData.datasets[0].data[i]).dir"
              :title="$t('ReportBuilder.compare.kpiHint', {
                value: formatKpiValue(kpiDelta(label, chartData.datasets[0].data[i]).prev),
                period: comparePeriodShortLabel
              })"
            >
              {{ kpiDelta(label, chartData.datasets[0].data[i]).text }}
              <span class="kpi-delta-period">{{ comparePeriodShortLabel }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Geo map (choropleth by country) -->
      <div v-else-if="isMapType">
        <GeoMap
          v-if="!loading && rawChartData"
          :items="rawChartData"
          :source-request="reportData.source_request"
        />
      </div>
    </template>

    <!-- Custom legend: colored badge pills below the chart. Border + tint follow the
         series color (never hardcoded); clicking a badge toggles that series.
         Hidden (inactive) series are tucked behind a "+N" badge instead of shown
         inline dimmed, so a long tail of toggled-off trends doesn't bloat the legend. -->
    <div v-if="showLegend" class="chart-legend">
      <button
        v-for="item in visibleLegendItems"
        :key="item.index"
        type="button"
        class="chart-legend__item"
        :style="legendBadgeStyle(item)"
        @click="toggleDataset(item.key)"
      >
        <span class="chart-legend__dot" :style="{ backgroundColor: item.color }"></span>
        <span class="chart-legend__label">{{ item.label }}</span>
      </button>
      <el-popover
        v-if="hiddenLegendItems.length"
        placement="bottom-start"
        trigger="click"
        popper-class="rb-chart-legend-popover"
        :width="240"
      >
        <template #reference>
          <button type="button" class="chart-legend__item chart-legend__more">
            {{ $t('ReportBuilder.chart.moreTrends', { count: hiddenLegendItems.length }) }}
          </button>
        </template>
        <div class="chart-legend__hidden-list">
          <button
            v-for="item in hiddenLegendItems"
            :key="item.index"
            type="button"
            class="chart-legend__item is-hidden"
            :style="legendBadgeStyle(item)"
            @click="toggleDataset(item.key)"
          >
            <span class="chart-legend__dot" :style="{ backgroundColor: item.color }"></span>
            <span class="chart-legend__label">{{ item.label }}</span>
          </button>
        </div>
      </el-popover>
    </div>

    <div v-if="loading" class="d-flex justify-content-center h-100 align-items-center">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue'
import { Line, Bar, Pie } from 'vue-chartjs'
import 'chartjs-adapter-date-fns';
import 'chart.js/auto'
// Registered locally (passed via the Pie component's `plugins` prop) rather than
// globally, so it never affects Line/Bar tooltips or scales.
import ChartDataLabels from 'chartjs-plugin-datalabels'
import ControlChart from "./_control_chart";
// Loaded lazily: pulls in chartjs-chart-geo + world topojson only when a map is shown
const GeoMap = defineAsyncComponent(() => import('./_geo_map.vue'))
import DateRange from "@/views/report_builder/builder/_dateRange.vue"
import CompareControl from "./_compare_control.vue"
import HardcodedDateWarning from "@/views/report_builder/builder/_hardcodedDateWarning.vue"
import { useEvents } from "@/composables/useEvents";
import { RETENTION_PALETTE } from "@/helpers/retentionPalette";
import { getCategoryStyle, categoryLabel } from "@/helpers/reports/categoryStyles";
import { SERIES_PALETTE, UTM_PALETTE } from "@/helpers/reports/palette";
import { normalizeCompare, isCompareSupported, formatDeltaPct, shiftDateKeyForward, shiftDateKeyBack } from "@/helpers/reports/compare";
import { isUtmTrendsBreakdown, effectiveChartType } from "@/helpers/reports/utmTrends";
import { isRetentionUtmBreakdown, aggregateRetentionByLabel } from "@/helpers/reports/retentionUtm";


// Maps lineStyle key to Chart.js borderDash array
const LINE_STYLE_DASH = {
  solid: [],
  dashed: [8, 4],
  dotted: [2, 4],
  dashdot: [8, 4, 2, 4]
}

// Converts array of values to running cumulative sum
function toCumulative(arr) {
  let sum = 0
  return (arr || []).map(v => {
    sum += (v == null || isNaN(v)) ? 0 : v
    return sum
  })
}

// Add alpha to hex color: #rrggbb → rgba(r,g,b,alpha)
function hexToRgba(hex, alpha) {
  const h = hex.replace('#', '')
  const r = parseInt(h.substring(0, 2), 16)
  const g = parseInt(h.substring(2, 4), 16)
  const b = parseInt(h.substring(4, 6), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

export default {
  name: "chart",
  components: { CLine: Line, Bar, Pie, ControlChart, DateRange, CompareControl, GeoMap, HardcodedDateWarning },
  inject: {
    injectedReportData: {
      from: 'injectedReportData',
      default: null
    },
    injectedExtraParams: {
      from: 'injectedExtraParams',
      default: null
    }
  },
  setup() {
    const { getEventName: resolveEventName } = useEvents();
    return { resolveEventName };
  },
  props: {
    hideControls: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      chartStyles: {
        height: '100%',
        position: 'relative',
        width: '100%'
      },
    }
  },
  computed: {
    reportData() {
      return this.injectedReportData || this.$store.state.ReportFromBuilder
    },
    // "Is this a UTM-breakdown trends report" and "which line/bar type to render" are
    // independent questions now — see helpers/reports/utmTrends.js.
    isUtmMode() {
      return isUtmTrendsBreakdown(this.reportData)
    },
    effectiveTypeChart() {
      return effectiveChartType(this.reportData)
    },
    isLineType() {
      const t = this.effectiveTypeChart
      return t === 'line' || t === 'area' || t === 'line_cumulative' || t === 'retention'
    },
    isBarType() {
      const t = this.effectiveTypeChart
      return t === 'bar' || t === 'bar_stacked'
    },
    isHorizontalBarType() {
      return this.reportData.type_chart === 'bar_horizontal'
    },
    isPieType() {
      return this.reportData.type_chart === 'pie'
    },
    isNumberType() {
      return this.reportData.type_chart === 'number'
    },
    isMapType() {
      return this.reportData.type_chart === 'map'
    },
    // Raw backend rows before any chart.js transformation (used by the geo map)
    rawChartData() {
      return this.injectedReportData
        ? this.injectedReportData.chartData
        : this.$store.state.ReportFromBuilder.chartData
    },
    // --- Сравнение периодов ---
    // Строки второго запроса (тот же отчёт, сдвинутый период). Приходят из стора либо
    // из локального состояния виджета — ровно как основные.
    compareRawData() {
      return this.reportData.compareData
    },
    compareConfig() {
      return normalizeCompare(this.reportData.visual)
    },
    compareActive() {
      return !!this.compareConfig
        && isCompareSupported(this.reportData.type, this.reportData.type_chart)
        && Array.isArray(this.compareRawData)
        && this.compareRawData.length > 0
    },
    // Короткая подпись для легенды и тултипа: «пред. период» / «год назад» / даты
    comparePeriodShortLabel() {
      const mode = this.compareConfig?.mode
      if (mode === 'previous_year') return this.$t('ReportBuilder.compare.previousYearShort')
      if (mode === 'custom') return this.$t('ReportBuilder.compare.customShort')
      return this.$t('ReportBuilder.compare.previousShort')
    },
    chartOptions() {
      const typeChart = this.reportData.type_chart
      const isStacked = typeChart === 'bar_stacked'
      const isHorizontal = typeChart === 'bar_horizontal'
      const isPie = typeChart === 'pie'

      const visual = this.reportData.visual || {}
      const hideLegend = !!visual.hideLegend
      const hideDateLabels = !!visual.hideDateLabels
      const gridStyle = visual.gridStyle || 'full'
      const showXGrid = gridStyle === 'full' || gridStyle === 'vertical'
      const showYGrid = gridStyle === 'full' || gridStyle === 'horizontal'
      // Шкала значений: 'zero' — всегда от нуля (по умолчанию), 'auto' — подстраивается
      // под диапазон данных. Ряд вида 2374→2397 на шкале от нуля выглядит прямой линией.
      const autoScale = visual.yAxisScale === 'auto'

      if (isPie) {
        const showValues = !!visual.showValues
        return {
          maintainAspectRatio: false,
          responsive: true,
          plugins: {
            // Custom HTML legend (badge pills) replaces Chart.js' canvas legend, same as line/bar
            legend: { display: !hideLegend && !this.useCustomLegend },
            tooltip: { enabled: true },
            // Registered locally on the Pie component only (see `pieChartPlugins`)
            datalabels: {
              display: showValues,
              color: '#fff',
              font: { weight: '600', size: 12 },
              formatter: (value) => this.formatKpiValue(value)
            }
          }
        }
      }

      if (isHorizontal) {
        return {
          maintainAspectRatio: false,
          responsive: true,
          indexAxis: 'y',
          scales: {
            x: autoScale
              ? { grace: '5%', grid: { display: showXGrid } }
              : { beginAtZero: true, grid: { display: showXGrid } },
            y: { ticks: { autoSkip: false }, grid: { display: showYGrid } }
          },
          plugins: {
            legend: { display: false },
            tooltip: { enabled: true }
          }
        }
      }

      return {
        maintainAspectRatio: false,
        responsive: true,
        tension: 0.1,
        scales: {
          y: {
            stacked: isStacked,
            grid: { display: showYGrid },
            // grace — отступ от крайних точек, чтобы линия не липла к рамке
            ...(autoScale ? { grace: '5%' } : { suggestedMin: 0 }),
          },
          x: {
            stacked: isStacked,
            grid: { display: showXGrid },
            ticks: {
              display: !hideDateLabels,
              callback: (value, index) => {
                const label = this.chartData?.labels?.[index]
                if (!label) return value

                const granularity = this.reportData.source_request?.granularity || 'day'
                if (typeChart === 'retention') {
                  return label
                }

                const date = new Date(label)
                const monthNamesShort = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

                if (granularity === 'month') {
                  return monthNamesShort[date.getMonth()] + ' ' + date.getFullYear()
                }
                if (granularity === 'week' || granularity === 'day') {
                  return monthNamesShort[date.getMonth()] + ' ' + date.getDate()
                }
                if (granularity === 'hour') {
                  const df = this.reportData.settings?.params?.[':date_from']
                  const dt = this.reportData.settings?.params?.[':date_to']
                  const isMoreThanDay = (df, dt) => {
                    if (df === 'today' || df === '-24h') return false
                    if (df?.startsWith('-') && df?.endsWith('d')) return parseInt(df.substring(1)) > 1
                    if (df?.length > 10 && dt?.length > 10) return (new Date(dt) - new Date(df)) > 86400000
                    return true
                  }
                  if (isMoreThanDay(df, dt)) {
                    return monthNamesShort[date.getMonth()] + ' ' + date.getDate()
                  } else {
                    return String(date.getHours()).padStart(2, '0') + ':00'
                  }
                }
                if (granularity === 'minute') {
                  if (date.getMinutes() % 30 === 0) {
                    return String(date.getHours()).padStart(2, '0') + ':' + String(date.getMinutes()).padStart(2, '0')
                  }
                  return ''
                }
                return value
              }
            }
          }
        },
        plugins: {
          // When a custom HTML legend is rendered below the chart (badge pills),
          // suppress Chart.js' built-in canvas legend to avoid duplication.
          legend: { display: !hideLegend && !this.useCustomLegend },
          tooltip: {
            enabled: true,
            mode: 'index',
            intersect: false,
            callbacks: {
              title: (tooltipItems) => {
                const index = tooltipItems[0].dataIndex
                const label = this.chartData?.labels?.[index]
                if (!label) return ''

                const granularity = this.reportData.source_request?.granularity || 'day'
                const base = this.formatBucketTitle(label, granularity)
                if (!this.compareActive) return base

                // При сравнении в заголовке обе даты: текущий bucket и его двойник
                const cmpDs = this.chartData?.datasets?.find(d => d.isCompare)
                const cmpLabel = cmpDs?.compareDates?.[index]
                if (!cmpLabel) return base
                return base + '  ·  ' + this.$t('ReportBuilder.compare.vs') + ' ' +
                  this.formatBucketTitle(cmpLabel, granularity)
              },
              // Ставится только при активном сравнении: иначе оставляем формат Chart.js по умолчанию
              label: this.compareActive ? (ctx) => {
                const ds = ctx.dataset
                const raw = ctx.parsed.y
                const shown = (raw === null || raw === undefined) ? '—' : raw
                if (!ds.isCompare) return ds.label + ': ' + shown
                const current = this.chartData?.datasets?.[ds.baseIndex]?.data?.[ctx.dataIndex]
                const delta = formatDeltaPct(current, raw)
                return ds.label + ': ' + shown + (delta ? ' (' + delta + ')' : '')
              } : undefined
            }
          }
        }
      }
    },
    requestVisualSettings() {
      return (this.reportData.source_request?.requests || []).map(r => ({
        color: r.color,
        lineWidth: r.lineWidth,
        lineStyle: r.lineStyle,
        lineTension: r.lineTension,
        showPoints: r.showPoints,
        fill: r.fill,
        chartType: r.chartType,
      }))
    },
    // Per-series line/bar override только имеет смысл когда сам отчёт line или bar —
    // у area/cumulative/stacked/retention/utm_trends смешивать типы нет смысла
    perTrendChartTypeAllowed() {
      const t = this.reportData.type_chart
      return t === 'line' || t === 'bar'
    },
    chartData() {
      const main = this.handlerChartData(this.rawChartData, this.requestVisualSettings)
      return this.withCompareDatasets(main)
    },
    // KPI: сравнительные значения по подписи серии (по индексу нельзя — при разбивке
    // порядок строк в ответе задан суммой и между периодами отличается)
    compareKpiByLabel() {
      if (!this.compareActive || !this.isNumberType) return null
      const agg = this.buildAggregatedData(this.compareRawData, SERIES_PALETTE, 'number')
      if (!agg) return null
      const map = {}
      agg.labels.forEach((label, i) => { map[label] = agg.datasets[0].data[i] })
      return map
    },
    // What's actually drawn in the pie: hidden slices (toggled off via the legend) are
    // dropped here, while chartData itself stays full so legendItems can still list them
    // (struck-through, under "+N") for re-enabling.
    pieChartData() {
      if (!this.isPieType || !this.chartData) return this.chartData
      const hidden = this.reportData.hiddenDatasets || []
      if (!hidden.length) return this.chartData
      const ds = this.chartData.datasets[0]
      const labels = []
      const values = []
      const colors = []
      this.chartData.labels.forEach((label, i) => {
        if (hidden.includes(i)) return
        labels.push(label)
        values.push(ds.data[i])
        colors.push(Array.isArray(ds.backgroundColor) ? ds.backgroundColor[i] : ds.backgroundColor)
      })
      return { labels, datasets: [{ ...ds, data: values, backgroundColor: colors }] }
    },
    // Passed to the Pie component's `plugins` prop so datalabels only ever apply to
    // pie charts, never to Line/Bar (which share the same global chart.js/auto registry).
    pieChartPlugins() {
      return [ChartDataLabels]
    },
    isAllZeroData() {
      // A KPI of 0 is meaningful — never replace it with the empty state
      if (this.isNumberType) return false
      const cd = this.chartData
      if (!cd || !Array.isArray(cd.datasets)) return false
      return cd.datasets.every(ds =>
        !Array.isArray(ds.data) || ds.data.every(v => !v || v === 0)
      )
    },
    // Custom HTML legend (badge pills below the chart) replaces Chart.js' canvas
    // legend for the common series-based types where dataset index === hiddenDatasets
    // index (pie keys off slice index the same way; retention's mean+cohort datasets
    // follow the same index convention).
    useCustomLegend() {
      const t = this.effectiveTypeChart
      const supported = t === 'line' || t === 'area' || t === 'line_cumulative' || t === 'bar' || t === 'bar_stacked' || t === 'pie' || t === 'retention'
      return supported && !this.reportData.visual?.hideLegend
    },
    showLegend() {
      return this.useCustomLegend && !this.loading && this.chartData
        && Array.isArray(this.chartData.datasets) && this.chartData.datasets.length > 0
        && !this.isAllZeroData
    },
    legendItems() {
      if (!this.chartData || !Array.isArray(this.chartData.datasets)) return []
      const hidden = this.reportData.hiddenDatasets || []

      // Pie: one item per slice (label), not per dataset — there's only ever one
      // dataset, with per-slice colors in its backgroundColor array.
      if (this.reportData.type_chart === 'pie') {
        const ds = this.chartData.datasets[0]
        if (!ds) return []
        return (this.chartData.labels || []).map((label, i) => ({
          index: i,
          key: i,
          label,
          color: (Array.isArray(ds.backgroundColor) ? ds.backgroundColor[i] : ds.backgroundColor) || '#909399',
          hidden: hidden.includes(i)
        }))
      }

      return this.chartData.datasets
        .map((ds, i) => ({ ds, i }))
        // Сравнительные серии не получают своих пунктов легенды: их видимость привязана
        // к основной серии, а собственный пункт сдвинул бы индексы hiddenDatasets.
        .filter(({ ds }) => !ds.isCompare)
        .map(({ ds, i }) => {
          const color = ds.borderColor || ds.backgroundColor || '#909399'
          // Наборы с раскрывающимся деревом (UTM-тренды, ретеншен с UTM-разбивкой)
          // переключаются по groupKey, совпадающему с ключом строки таблицы: при
          // раскрытии ветки порядок наборов меняется, и индекс перестал бы указывать
          // на ту же линию. Остальные отчёты — по индексу, как раньше.
          const key = ds.groupKey !== undefined ? ds.groupKey : i
          return {
            index: i,
            key,
            label: ds.label,
            color: typeof color === 'string' ? color : '#909399',
            hidden: hidden.includes(key)
          }
        })
    },
    // Split so inactive series collapse behind a "+N" badge instead of cluttering
    // the legend inline — the badge's popover lists them for re-activation.
    visibleLegendItems() {
      return this.legendItems.filter(item => !item.hidden)
    },
    hiddenLegendItems() {
      return this.legendItems.filter(item => item.hidden)
    }
  },
  watch: {
    // Новый прогон ретеншена: как и в _utm_report.vue, по умолчанию видны только
    // топ-5 линий, остальные сворачиваются под "+N ещё" в легенде.
    'reportData.chartData'() {
      this.applyDefaultRetentionVisibility()
    }
  },
  methods: {
    // На новых данных ретеншена оставляет видимыми только первые 5 когорт (или,
    // при UTM-разбивке, топ-5 меток по размеру — aggregateRetentionByLabel уже
    // отдаёт их отсортированными по size убыв.), остальные прячет за "+N ещё" —
    // тот же приём, что applyTop5Visibility в _utm_report.vue для трендов.
    applyDefaultRetentionVisibility() {
      if (this.reportData.type_chart !== 'retention') return
      const data = Array.isArray(this.rawChartData) ? this.rawChartData : []
      if (!data.length) return

      let hiddenKeys
      if (isRetentionUtmBreakdown(this.reportData)) {
        const rows = aggregateRetentionByLabel(data, {
          maxIntervals: this.reportData.source_request?.retention_total_intervals || 8,
          mode: this.reportData.source_request?.mean_retention_calculation || 'simple',
        })
        hiddenKeys = rows.slice(5).map(row => row.key)
      } else {
        // Ключи когорт — 1-based индекс (0 зарезервирован за средней линией),
        // см. buildRetentionData.
        hiddenKeys = data.slice(5).map((_, i) => i + 6)
      }

      if (this.injectedReportData) {
        this.injectedReportData.hiddenDatasets = hiddenKeys
      } else {
        this.$store.commit('ReportFromBuilder/setHiddenDatasets', hiddenKeys)
      }
    },
    // Заголовок тултипа для одного bucket'а. Вынесено из callbacks.title, чтобы тем же
    // кодом форматировать дату сравнительного периода.
    formatBucketTitle(label, granularity) {
      const date = new Date(label)
      if (isNaN(date.getTime())) return String(label)
      const monthNamesShort = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

      if (granularity === 'week') {
        const endDate = new Date(date)
        endDate.setDate(date.getDate() + 6)
        return monthNamesShort[date.getMonth()] + ' ' + date.getDate() + ' - ' +
          (date.getMonth() !== endDate.getMonth() ? monthNamesShort[endDate.getMonth()] + ' ' : '') +
          endDate.getDate() + ', ' + date.getFullYear()
      }
      return monthNamesShort[date.getMonth()] + ' ' + date.getDate() + ', ' + date.getFullYear() +
        ' ' + String(date.getHours()).padStart(2, '0') + ':' + String(date.getMinutes()).padStart(2, '0')
    },

    // Badge styling from the series color: solid dot, colored border, faint tint fill.
    legendBadgeStyle(item) {
      const color = item.color
      const isHex = typeof color === 'string' && /^#([0-9a-f]{6})$/i.test(color)
      return {
        borderColor: color,
        backgroundColor: isHex ? hexToRgba(color, 0.1) : 'transparent',
        color: 'var(--ui-text-2, #374151)'
      }
    },
    // Toggle a series' visibility. Works both in dashboard/isolated mode (mutate the
    // injected reactive state) and in the standalone builder (Vuex).
    toggleDataset(key) {
      if (this.injectedReportData) {
        if (!Array.isArray(this.injectedReportData.hiddenDatasets)) {
          this.injectedReportData.hiddenDatasets = []
        }
        const arr = this.injectedReportData.hiddenDatasets
        const pos = arr.indexOf(key)
        if (pos === -1) arr.push(key)
        else arr.splice(pos, 1)
      } else {
        this.$store.commit('ReportFromBuilder/toggleDatasetVisibility', key)
      }
    },
    /**
     * Дорисовывает к основным сериям их двойников за сравнительный период:
     * приглушённый цвет, пунктир, без точек. Выравнивание — по индексу bucket'а
     * (бэкенд строит ось X синтетически из :date_from/:date_to, поэтому ряды плотные),
     * но на равенство длин не полагаемся: недостающие точки — null.
     */
    withCompareDatasets(main) {
      if (!main || !this.compareActive) return main
      // Агрегатные виды (KPI/пирог/горизонтальные бары/карта) сравниваются иначе —
      // KPI показывает дельту прямо в плитке, остальные пока не поддержаны.
      if (this.isNumberType || this.isPieType || this.isHorizontalBarType || this.isMapType) return main

      // seriesKey → уже выровненный по оси основного периода массив значений
      const byKey = this.isUtmMode
        ? this.buildUtmCompareSeries(main.labels || [])
        : this.buildTrendsCompareSeries()
      if (!byKey || byKey.size === 0) return main

      const pointCount = (main.labels || []).length
      const suffix = ' · ' + this.comparePeriodShortLabel
      const compareLabels = this.compareAxisLabels(main.labels || [])
      const extra = []

      main.datasets.forEach((ds, i) => {
        const src = byKey.get(ds.seriesKey)
        if (!src) return
        const data = []
        for (let p = 0; p < pointCount; p++) {
          const v = src[p]
          data.push(v === undefined ? null : v)
        }
        const baseColor = typeof ds.borderColor === 'string' ? ds.borderColor : '#909399'
        const isHex = /^#([0-9a-f]{6})$/i.test(baseColor)
        extra.push({
          ...ds,
          label: ds.label + suffix,
          data,
          isCompare: true,
          baseIndex: i,
          // подписи bucket'ов сравнительного периода — для тултипа
          compareDates: compareLabels,
          borderColor: isHex ? hexToRgba(baseColor, 0.5) : baseColor,
          backgroundColor: isHex ? hexToRgba(baseColor, 0.18) : baseColor,
          fill: false,
          borderDash: [5, 4],
          pointRadius: 0,
          pointHoverRadius: 3,
          // видимость всегда следует за основной серией: у сравнения нет своего
          // переключателя в легенде, иначе поехали бы индексы hiddenDatasets
          hidden: ds.hidden
        })
      })

      if (!extra.length) return main
      return { labels: main.labels, datasets: [...main.datasets, ...extra] }
    },

    /**
     * Метки сравнительного периода, параллельные оси основного (для тултипа).
     * При известном сдвиге считаются из основной оси — это точнее, чем брать ось
     * сравнительного ответа: у utm_trends она разрежена и её индексы не совпадают.
     */
    compareAxisLabels(mainLabels) {
      const shift = this.reportData.comparePeriod?.shift || null
      if (shift) return mainLabels.map(label => shiftDateKeyBack(label, shift) || label)
      if (this.isUtmMode) {
        const days = new Set()
        for (const row of (this.compareRawData || [])) {
          if (row?.day_start) days.add(row.day_start)
        }
        return Array.from(days).sort()
      }
      return this.compareRawData?.[0]?.date || []
    },

    /**
     * Обычные тренды: ось плотная и одинаковой длины у обоих периодов, поэтому
     * значения берутся как есть и совмещаются по индексу bucket'а.
     */
    buildTrendsCompareSeries() {
      const compare = this.handlerChartData(this.compareRawData, this.requestVisualSettings)
      if (!compare || !Array.isArray(compare.datasets)) return null
      const byKey = new Map()
      compare.datasets.forEach(ds => {
        if (ds.seriesKey !== undefined && !byKey.has(ds.seriesKey)) byKey.set(ds.seriesKey, ds.data)
      })
      return byKey
    },

    /**
     * utm_trends: ряды плоские (строка на UTM×день), ось строится из самих данных и
     * РАЗРЕЖЕНА — дни без событий отсутствуют, причём у двух периодов пропуски разные.
     * Поэтому выравнивать по индексу нельзя: переносим каждый день сравнительного
     * периода вперёд на тот же сдвиг, которым период и строился, и кладём в его слот
     * на основной оси. Для произвольного периода сдвига нет — там по порядку дней.
     */
    buildUtmCompareSeries(labels) {
      const rows = this.compareRawData
      if (!Array.isArray(rows) || !rows.length || !labels.length) return null

      const metricKey = 'metric_' + (this.reportData.utmActiveMetric || 0)
      const shift = this.reportData.comparePeriod?.shift || null

      const groups = new Map()
      const days = new Set()
      for (const row of rows) {
        const groupKey = (row.utm_id != null) ? String(row.utm_id) : (row.utm_name || 'none')
        if (!groups.has(groupKey)) groups.set(groupKey, {})
        const byDay = groups.get(groupKey)
        const day = row.day_start
        days.add(day)
        byDay[day] = (byDay[day] || 0) + (Number(row[metricKey]) || 0)
      }

      const sortedDays = Array.from(days).sort()
      const dayToLabel = new Map()
      if (shift) {
        for (const day of sortedDays) {
          const mapped = shiftDateKeyForward(day, shift)
          if (mapped) dayToLabel.set(day, mapped)
        }
      } else {
        sortedDays.forEach((day, i) => {
          if (labels[i] !== undefined) dayToLabel.set(day, labels[i])
        })
      }

      const labelIndex = new Map(labels.map((label, i) => [label, i]))
      const out = new Map()
      for (const [groupKey, byDay] of groups) {
        // ноль, а не null: день внутри периода без событий — это честный ноль,
        // ровно как в основной серии (group.byDay[day] || 0)
        const values = new Array(labels.length).fill(0)
        for (const [day, value] of Object.entries(byDay)) {
          const label = dayToLabel.get(day)
          const idx = label !== undefined ? labelIndex.get(label) : undefined
          if (idx !== undefined) values[idx] += value
        }
        out.set(groupKey, values)
      }
      return out
    },

    /** Дельта KPI-плитки относительно сравнительного периода. */
    kpiDelta(label, current) {
      const prev = this.compareKpiByLabel?.[label]
      if (prev === undefined || prev === null) return null
      const text = formatDeltaPct(current, prev)
      const cur = Number(current) || 0
      const p = Number(prev) || 0
      return {
        text: text || this.$t('ReportBuilder.compare.noBase'),
        dir: cur > p ? 'up' : (cur < p ? 'down' : 'flat'),
        prev: p
      }
    },

    handlerChartData(data, visualSettings) {
      if (!data || data.length === 0) return null

      const typeChart = this.reportData.type_chart
      const presetColors = SERIES_PALETTE

      // Retention chart
      if (typeChart === 'retention') {
        return this.buildRetentionData(data)
      }

      // UTM trends — flat rows grouped by utm_id into separate line/bar datasets. Rendered
      // as CLine or Bar depending on effectiveTypeChart (see isLineType/isBarType) — this
      // function only shapes the data, not the mark type.
      if (this.isUtmMode) {
        return this.buildUtmTrendsData(data, UTM_PALETTE)
      }

      // Pie / Horizontal bar / KPI number / Map — aggregate totals per dataset, no date axis
      if (typeChart === 'pie' || typeChart === 'bar_horizontal' || typeChart === 'number' || typeChart === 'map') {
        return this.buildAggregatedData(data, presetColors, typeChart)
      }

      const isArea = typeChart === 'area'
      const isCumulative = typeChart === 'line_cumulative'
      // Chart.js dataset-level type: base defaults to the report's own type, but for
      // plain line/bar reports a series can override it individually (mixed chart)
      const baseDatasetType = (typeChart === 'bar' || typeChart === 'bar_stacked') ? 'bar' : 'line'
      const perTrendAllowed = this.perTrendChartTypeAllowed

      const datasets = []

      const formulas = (this.reportData.source_request?.formulas || []).filter(f => f?.expression)
      const hasActiveFormulas = formulas.length > 0
      const hasGroupings = data.some(d => d.separators && d.separators.length > 0)
      const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'

      for (let i = 0; i < data.length; i++) {
        const item = data[i]
        const reqIdx = (item.request_index !== undefined) ? item.request_index : i
        const request = this.reportData.source_request?.requests?.[reqIdx] || null
        const vis = visualSettings?.[reqIdx] || null

        const label = this.buildSeriesLabel(request, item, reqIdx)

        // Приоритет: цвет, закреплённый за значением разбивки → цвет серии → палитра.
        // При разбивке серий больше, чем requests, поэтому цвет запроса там не применим.
        const categoryColor = getCategoryStyle(this.reportData.visual, item.separators).color
        const customColor = vis?.color ?? request?.color
        const color = categoryColor || (hasGroupings ? presetColors[i % presetColors.length] : (customColor || presetColors[i % presetColors.length]))

        const rawData = isCumulative ? toCumulative(item.total) : item.total

        const fillEnabled = vis?.fill !== undefined ? vis.fill : isArea
        const datasetType = perTrendAllowed ? (vis?.chartType || request?.chartType || baseDatasetType) : baseDatasetType
        datasets.push({
          label,
          type: datasetType,
          // Ключ серии для сопоставления с рядом сравнительного периода: порядок строк
          // в ответе задан arraySum(total) desc, поэтому при разбивке он может отличаться
          // между периодами — сопоставлять по индексу нельзя.
          seriesKey: reqIdx + '\x00' + (item.separators || []).join('\x00'),
          data: rawData,
          borderColor: color,
          backgroundColor: fillEnabled ? hexToRgba(color.startsWith('#') ? color : '#409EFF', 0.2) : color,
          fill: fillEnabled,
          tension: vis?.lineTension === 'straight' ? 0 : (request?.lineTension === 'straight' ? 0 : 0.3),
          borderWidth: vis?.lineWidth ?? request?.lineWidth ?? 1,
          borderDash: LINE_STYLE_DASH[vis?.lineStyle ?? request?.lineStyle ?? 'solid'] ?? [],
          pointRadius: (vis?.showPoints ?? request?.showPoints) ? 3 : 0,
          pointHoverRadius: (vis?.showPoints ?? request?.showPoints) ? 5 : 3,
          // Hide regular trends when formula mode is active
          hidden: hasActiveFormulas || (this.reportData.hiddenDatasets || []).includes(i)
        })
      }

      // Formula datasets
      for (let fIdx = 0; fIdx < formulas.length; fIdx++) {
        const formula = formulas[fIdx]
        const formulaColor = formula.color || '#E6A23C'
        const fillEnabled = formula.fill !== undefined ? formula.fill : isArea
        const formulaDatasetType = perTrendAllowed ? (formula.chartType || baseDatasetType) : baseDatasetType
        const formulaDataset = (label, formulaData, dIdx, sepKey = '') => ({
          label,
          type: formulaDatasetType,
          seriesKey: 'f' + fIdx + '\x00' + sepKey,
          data: formulaData,
          borderColor: formulaColor,
          backgroundColor: fillEnabled ? hexToRgba(formulaColor.startsWith('#') ? formulaColor : '#E6A23C', 0.2) : formulaColor,
          fill: fillEnabled,
          tension: formula.lineTension === 'straight' ? 0 : 0.3,
          borderWidth: formula.lineWidth ?? 1,
          borderDash: LINE_STYLE_DASH[formula.lineStyle ?? 'dashed'] ?? [6, 4],
          pointRadius: formula.showPoints ? 3 : 0,
          pointHoverRadius: formula.showPoints ? 5 : 3,
          hidden: (this.reportData.hiddenDatasets || []).includes(dIdx)
        })

        if (hasGroupings) {
          const groupMap = new Map()
          data.forEach((item, i) => {
            const reqIdx = (item.request_index !== undefined) ? item.request_index : i
            const letter = letters[reqIdx]
            const sepKey = (item.separators || []).join('\x00')
            if (!groupMap.has(sepKey)) {
              groupMap.set(sepKey, { separators: item.separators || [], dataByLetter: {} })
            }
            const group = groupMap.get(sepKey)
            if (letter && !group.dataByLetter[letter]) {
              group.dataByLetter[letter] = isCumulative ? toCumulative(item.total) : (item.total || [])
            }
          })
          const numPoints = data[0].total?.length || 0
          groupMap.forEach((group) => {
            const formulaData = []
            for (let i = 0; i < numPoints; i++) {
              const varMap = {}
              for (const [letter, values] of Object.entries(group.dataByLetter)) {
                varMap[letter] = values[i] ?? 0
              }
              formulaData.push(this.evaluateFormula(formula.expression, varMap))
            }
            const dIdx = datasets.length
            const sepSuffix = group.separators.length > 0 ? ` - ${group.separators.join(' - ')}` : ''
            datasets.push(formulaDataset(
              (formula.label || 'Formula') + sepSuffix,
              formulaData,
              dIdx,
              group.separators.join('\x00')
            ))
          })
        } else {
          const requestDataMap = {}
          data.forEach((item, i) => {
            const reqIdx = (item.request_index !== undefined) ? item.request_index : i
            const letter = letters[reqIdx]
            if (letter && requestDataMap[letter] === undefined) {
              requestDataMap[letter] = isCumulative ? toCumulative(item.total) : (item.total || [])
            }
          })
          const numPoints = data[0].total?.length || 0
          const formulaData = []
          for (let i = 0; i < numPoints; i++) {
            const varMap = {}
            for (const [letter, values] of Object.entries(requestDataMap)) {
              varMap[letter] = values[i] ?? 0
            }
            formulaData.push(this.evaluateFormula(formula.expression, varMap))
          }
          datasets.push(formulaDataset(formula.label || 'Formula', formulaData, datasets.length))
        }
      }

      return {
        labels: data[0].date,
        datasets
      }
    },

    buildUtmTrendsData(data, paletteColors) {
      if (!data || data.length === 0) return null

      const metricKey = 'metric_' + (this.reportData.utmActiveMetric || 0)
      const extraDataMap = this.reportData.utmExpandedRows || {}
      const extraData = Object.values(extraDataMap).flat()

      // Build a group map from a flat rows array
      const buildGroups = (rows) => {
        const groups = {}
        const days = new Set()
        for (const row of rows) {
          const groupKey = (row.utm_id != null) ? String(row.utm_id) : (row.utm_name || 'none')
          if (!groups[groupKey]) {
            groups[groupKey] = {
              groupKey,
              utm_name: row.utm_name || String(row.utm_id) || 'none',
              byDay: {},
              totalSum: 0
            }
          }
          const v = Number(row[metricKey]) || 0
          const dayKey = row.day_start
          groups[groupKey].byDay[dayKey] = (groups[groupKey].byDay[dayKey] || 0) + v
          groups[groupKey].totalSum += v
          days.add(dayKey)
        }
        return { groups, days }
      }

      const { groups: topGroups, days: topDays } = buildGroups(data)
      const { groups: childGroups, days: childDays } = buildGroups(extraData)

      // Children that share a key with top-level are already counted — skip them
      for (const key of Object.keys(childGroups)) {
        if (topGroups[key]) delete childGroups[key]
      }

      const allDays = new Set([...topDays, ...childDays])
      const sortedDays = Array.from(allDays).sort()
      const hiddenDatasets = this.reportData.hiddenDatasets || []

      // Color assignment: top-level first (desc total), then children (desc total).
      // This keeps top-level colors stable when drill-down rows are added.
      const sortedTop = Object.values(topGroups).sort((a, b) => b.totalSum - a.totalSum)
      const sortedChildren = Object.values(childGroups).sort((a, b) => b.totalSum - a.totalSum)

      const datasets = [...sortedTop, ...sortedChildren].map((group, i) => ({
        label: group.utm_name,
        groupKey: group.groupKey,
        // Ключ для сопоставления с рядом сравнительного периода (см. withCompareDatasets)
        seriesKey: group.groupKey,
        data: sortedDays.map(day => group.byDay[day] || 0),
        borderColor: paletteColors[i % paletteColors.length],
        backgroundColor: paletteColors[i % paletteColors.length],
        fill: false,
        tension: 0.3,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        hidden: hiddenDatasets.includes(group.groupKey)
      }))

      return { labels: sortedDays, datasets }
    },

    buildAggregatedData(data, presetColors, typeChart) {
      const labels = []
      const values = []
      const colors = []

      for (let i = 0; i < data.length; i++) {
        const item = data[i]
        const reqIdx = (item.request_index !== undefined) ? item.request_index : i
        const request = this.reportData.source_request?.requests?.[reqIdx] || null

        const label = this.buildSeriesLabel(request, item, reqIdx)

        const hasGroupings = data.some(d => d.separators && d.separators.length > 0)
        const categoryColor = getCategoryStyle(this.reportData.visual, item.separators).color
        const customColor = request?.color
        const color = categoryColor || (hasGroupings ? presetColors[i % presetColors.length] : (customColor || presetColors[i % presetColors.length]))

        const total = (item.total || []).reduce((sum, v) => sum + ((v == null || isNaN(v)) ? 0 : v), 0)

        labels.push(label)
        values.push(total)
        colors.push(color)
      }

      if (typeChart === 'bar_horizontal') {
        // One dataset per bar so each bar gets its own color
        return {
          labels,
          datasets: [{
            data: values,
            backgroundColor: colors,
            borderColor: colors,
            borderWidth: 1
          }]
        }
      }

      // pie
      return {
        labels,
        datasets: [{
          data: values,
          backgroundColor: colors,
          borderWidth: 1
        }]
      }
    },

    /**
     * Ретеншен с UTM-разбивкой: линия = метка, а не когорта.
     *
     * Когорты внутри метки сворачиваются в одну усреднённую кривую — иначе на
     * графике оказалось бы «меток × когорт» линий, ради чего разбивку никто не
     * включает. Матрица по когортам остаётся доступной провалом внутрь метки.
     */
    buildRetentionUtmData(data) {
      const maxIntervals = this.reportData.source_request?.retention_total_intervals || 8
      const labels = []
      for (let i = 0; i < maxIntervals; i++) {
        labels.push(`${this.$t('ReportBuilder.constructor.' + (this.reportData.source_request?.granularity || 'day'), { n: i })}`)
      }

      const aggregate = (source) => aggregateRetentionByLabel(source, {
        maxIntervals,
        mode: this.reportData.source_request?.mean_retention_calculation || 'simple',
      })

      const rows = aggregate(data)

      // Метки раскрытых веток дерева приезжают отдельными запросами и лежат в
      // utmExpandedRows (тот же механизм, что у UTM-трендов). Каждая ветка
      // агрегируется своим вызовом: смешивать уровни в одном прогоне нельзя —
      // метки разной глубины поделили бы одну группу.
      //
      // Обход повторяет порядок строк в _retention_table.vue (родитель, следом
      // его дети), потому что цвет назначается по порядковому номеру: разойдись
      // обходы — цвет линии перестал бы совпадать с цветом строки таблицы.
      const expandedMap = this.reportData.utmExpandedRows || {}
      const ordered = []
      const walk = (list, depth) => {
        list.forEach(row => {
          ordered.push({ row, depth })
          const children = expandedMap[row.key]
          if (children) {
            walk(aggregate(children), depth + 1)
          }
        })
      }
      walk(rows, 0)

      const retentionColors = this.reportData.visual?.retention_colors || {}
      const cohortStyles = this.reportData.visual?.retention_cohort_styles || {}
      const hiddenDatasets = this.reportData.hiddenDatasets || []

      // Средняя считается только по верхнему уровню: дети — это тот же трафик,
      // разложенный мельче, и их линии удвоили бы вес своих родителей.
      const meanIntervals = []
      for (let i = 0; i < maxIntervals; i++) {
        let sum = 0, count = 0
        rows.forEach(row => {
          if (row.intervals[i] !== null && row.intervals[i] !== undefined) {
            sum += row.intervals[i]
            count++
          }
        })
        meanIntervals.push(count > 0 ? Number((sum / count).toFixed(1)) : null)
      }

      const meanStyle = cohortStyles['__mean__'] || {}
      const datasets = [{
        label: this.$t('ReportBuilder.retention.mean'),
        // Видимость наборов ретеншена с разбивкой ключуется по groupKey, а не по
        // индексу: раскрытие ветки вставляет линии в середину списка.
        groupKey: '__mean__',
        data: meanIntervals,
        borderColor: RETENTION_PALETTE[0],
        backgroundColor: RETENTION_PALETTE[0],
        borderWidth: meanStyle.lineWidth ?? 2,
        borderDash: LINE_STYLE_DASH[meanStyle.lineStyle ?? 'dashed'] ?? [],
        tension: (meanStyle.lineTension ?? 'smooth') === 'straight' ? 0 : 0.3,
        pointRadius: (meanStyle.showPoints ?? false) ? 3 : 0,
        pointHoverRadius: (meanStyle.showPoints ?? false) ? 5 : 3,
        hidden: hiddenDatasets.includes('__mean__'),
      }]

      // Дети рисуются пунктиром, если пользователь не задал стиль сам: так на
      // графике видно, что линия — часть родительской, а не ещё один источник.
      ordered.forEach(({ row, depth }, i) => {
        const isChild = depth > 0
        const s = cohortStyles[row.key] || {}
        const color = retentionColors[row.key] || UTM_PALETTE[i % UTM_PALETTE.length]

        datasets.push({
          label: row.utm_name || this.$t('ReportBuilder.retention.noUtm'),
          groupKey: row.key,
          data: row.intervals,
          borderColor: color,
          backgroundColor: color,
          borderWidth: s.lineWidth ?? (isChild ? 1 : 2),
          borderDash: LINE_STYLE_DASH[s.lineStyle ?? (isChild ? 'dashed' : 'solid')] ?? [],
          tension: (s.lineTension ?? 'smooth') === 'straight' ? 0 : 0.3,
          pointRadius: (s.showPoints ?? false) ? 3 : 0,
          pointHoverRadius: (s.showPoints ?? false) ? 5 : 3,
          hidden: hiddenDatasets.includes(row.key),
        })
      })

      return { labels, datasets }
    },

    buildRetentionData(data) {
      if (isRetentionUtmBreakdown(this.reportData)) {
        return this.buildRetentionUtmData(data)
      }

      const labels = []
      const maxIntervals = this.reportData.source_request?.retention_total_intervals || 8
      for (let i = 0; i < maxIntervals; i++) {
        labels.push(`Day ${i}`)
      }

      const datasets = []
      const retentionColors = this.reportData.visual?.retention_colors || {}
      const cohortStyles = this.reportData.visual?.retention_cohort_styles || {}
      const hiddenDatasets = this.reportData.hiddenDatasets || []

      const getStyle = (key) => {
        const s = cohortStyles[key] || {}
        return {
          lineWidth: s.lineWidth ?? 1,
          borderDash: LINE_STYLE_DASH[s.lineStyle ?? 'solid'] ?? [],
          tension: (s.lineTension ?? 'smooth') === 'straight' ? 0 : 0.3,
          pointRadius: (s.showPoints ?? false) ? 3 : 0,
          pointHoverRadius: (s.showPoints ?? false) ? 5 : 3,
        }
      }

      // Mean line
      const meanIntervals = []
      for (let i = 0; i < maxIntervals; i++) {
        let sumPercent = 0, count = 0
        data.forEach(item => {
          const intervals = this.normalizeRetentionIntervals(item)
          if (intervals && intervals.length > i && intervals[i] !== null) {
            sumPercent += intervals[i]
            count++
          }
        })
        meanIntervals.push(count > 0 ? Number((sumPercent / count).toFixed(1)) : null)
      }

      const meanStyle = getStyle('__mean__')
      datasets.push({
        label: 'Mean Retention',
        data: meanIntervals,
        borderColor: RETENTION_PALETTE[0],
        backgroundColor: RETENTION_PALETTE[0],
        borderWidth: meanStyle.lineWidth,
        borderDash: meanStyle.borderDash,
        tension: meanStyle.tension,
        pointRadius: meanStyle.pointRadius,
        pointHoverRadius: meanStyle.pointHoverRadius,
        hidden: hiddenDatasets.includes(0)
      })

      data.forEach((item, i) => {
        const intervals = this.normalizeRetentionIntervals(item)
        if (intervals) {
          const cohortLabel = this.formatCohortDate(item)
          const cohortColor = (item.date && retentionColors[item.date])
            ? retentionColors[item.date]
            : RETENTION_PALETTE[(i + 1) % RETENTION_PALETTE.length]
          const s = getStyle(item.date)
          datasets.push({
            label: cohortLabel,
            data: intervals,
            borderColor: cohortColor,
            backgroundColor: cohortColor,
            borderWidth: s.lineWidth,
            borderDash: s.borderDash,
            tension: s.tension,
            pointRadius: s.pointRadius,
            pointHoverRadius: s.pointHoverRadius,
            hidden: hiddenDatasets.includes(i + 1)
          })
        }
      })

      return { labels, datasets }
    },

    normalizeRetentionIntervals(item) {
      if (item.intervals) return item.intervals
      if (item.values) {
        const size = item.values[0]?.count || 0
        return item.values.map(v => size > 0 ? Number(((v.count / size) * 100).toFixed(1)) : 0)
      }
      if (item.totals) {
        const size = item.totals[0] || 0
        return item.totals.map(t => size > 0 ? Number(((t / size) * 100).toFixed(1)) : 0)
      }
      return null
    },

    evaluateFormula(expression, varMap) {
      if (!/^[A-Za-z0-9+\-*/().\\s\s]+$/.test(expression)) return null
      let expr = expression.toUpperCase()
      for (const [letter, value] of Object.entries(varMap)) {
        const safeVal = (value === null || value === undefined || isNaN(value)) ? 0 : value
        expr = expr.replace(new RegExp(`\\b${letter}\\b`, 'g'), `(${safeVal})`)
      }
      try {
        // eslint-disable-next-line no-new-func
        const result = new Function(`return ${expr}`)()
        if (!isFinite(result)) return null
        return Math.round(result * 10000) / 10000
      } catch {
        return null
      }
    },

    formatKpiValue(value) {
      if (value === null || value === undefined || isNaN(value)) return '—'
      const n = Number(value)
      // Keep fractions only for small values (averages, ratios)
      const rounded = Math.abs(n) >= 100 ? Math.round(n) : Math.round(n * 100) / 100
      return rounded.toLocaleString()
    },

    // Подпись серии. В SQL-режиме request описывает не событие, а колонку результата:
    // агрегации там нет, а при единственной метрике имя колонки в подписи только мешает —
    // осмысленная подпись это значение разбивки («Мужской», а не «Пользователи - Мужской»).
    buildSeriesLabel(request, item, reqIdx) {
      if (!request) return 'Dataset ' + (reqIdx + 1)

      // Пользовательская подпись значения разбивки, если задана («Мужской» вместо «1»)
      const separators = categoryLabel(this.reportData.visual, item?.separators)

      if (request.type === 'sql_column') {
        const base = request.label || request.column || ('Dataset ' + (reqIdx + 1))
        if (!separators) return base
        const isSingleMetric = (this.reportData.source_request?.requests || []).length <= 1
        return isSingleMetric ? separators : `${base} - ${separators}`
      }

      const eventName = this.translateEventLabel(request.label || request.event_name || this.getEventName(this.resolveTemplateEventId(request.event_id)))
      let agrLabel = this.getAggregationLabel(request.agr)
      if (request.agr_field === 'events.value_amount') {
        const currency = this.$store.state.init.user?.report_currency
        if (currency) agrLabel = `${agrLabel}, ${currency}`
      }
      const label = `${eventName} (${agrLabel})`
      return separators ? `${label} - ${separators}` : label
    },

    getAggregationLabel(agr) {
      const labels = {
        'total': this.$t('ReportBuilder.action.total'),
        'total_uniq_user_id': this.$t('ReportBuilder.action.uniqueUsers'),
        'total_first': this.$t('ReportBuilder.action.first'),
        'total_repeat': this.$t('ReportBuilder.action.repeat'),
        'total_uniq_session_id': this.$t('ReportBuilder.action.uniqueSessions'),
        'total_uniq_app_session_id': this.$t('ReportBuilder.action.uniqueAppSessions'),
        'avg_user_id': this.$t('ReportBuilder.action.avg'),
        'min_user_id': this.$t('ReportBuilder.action.min'),
        'max_user_id': this.$t('ReportBuilder.action.max'),
        'median_user_id': this.$t('ReportBuilder.action.median'),
        'property_sum': this.$t('ReportBuilder.action.sum'),
        'property_avg': this.$t('ReportBuilder.action.avg'),
        'property_min': this.$t('ReportBuilder.action.min'),
        'property_max': this.$t('ReportBuilder.action.max'),
        'property_median': this.$t('ReportBuilder.action.median'),
      }
      return labels[agr] || agr
    },

    translateEventLabel(label) {
      if (!label) return label;
      return label.split(', ').map(name => {
        const trimmed = name.trim();
        const key = `Components.EventSelector.CustomEvents.${trimmed}.name`;
        return this.$te(key) ? this.$t(key) : trimmed;
      }).join(', ');
    },
    getEventName(eventId) {
      if (eventId === 'all' || eventId === null) {
        return this.$t("Components.EventSelector.AnyEvent")
      }
      if (Array.isArray(eventId)) {
        return eventId.map(id => this.getEventName(id)).join(', ')
      }
      const raw = this.resolveEventName(eventId)
      return this.translateEventLabel(raw)
    },
    // A dashboard widget's saved template holds the unresolved `:param` placeholder
    // (e.g. ':payment_event_id') — substitution only happens server-side when the
    // query executes. Resolve it back to the real event id via the dashboard's
    // extraParams so the label shows the chosen event's name, not the placeholder.
    resolveTemplateEventId(eventId) {
      if (typeof eventId !== 'string' || !eventId.startsWith(':')) return eventId
      const params = this.injectedExtraParams?.value
      const resolved = params?.[eventId]
      if (resolved === undefined || resolved === null) return eventId
      if (Array.isArray(resolved)) return resolved.length === 1 ? resolved[0] : (resolved.length ? resolved : eventId)
      return resolved
    },

    formatCohortDate(row) {
      if (!row.date) return row.label || ''
      const d = new Date(row.date)
      if (isNaN(d.getTime())) return row.label || row.date
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${days[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}`
    },
  }
}
</script>

<style scoped>
.main-chart {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.main-chart > div:not(.d-flex),
.main-chart > template > div {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

/* Embedded in a dashboard widget: drop the nested card chrome and share the widget's
   height with the data table below via a common flex-basis of 0, so extra height from
   resizing the widget actually grows the chart instead of only the table. min-height
   still floors it so the legend badges never overflow onto the table when space is tight. */
.main-chart--embedded {
  border: none;
  border-radius: 0;
  box-shadow: none;
  background: transparent;
  padding: 0 !important;
  flex: 1 1 0;
  min-height: 220px;
}

.main-chart--embedded > div:not(.d-flex):not(.chart-legend) {
  min-height: 160px;
}

/* Legend (badge pills) must not stretch like the plotting area does */
.main-chart > div.chart-legend {
  flex: 0 0 auto;
  min-height: 0;
  overflow: visible;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 8px;
  padding-top: 10px;
}

.chart-legend__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px 3px 8px;
  border: 1px solid transparent;
  border-radius: 999px;
  background: transparent;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  transition: opacity 0.15s, filter 0.15s;
}

.chart-legend__item:hover {
  filter: brightness(0.97);
}

.chart-legend__item.is-hidden {
  opacity: 0.45;
}

.chart-legend__item.is-hidden .chart-legend__label {
  text-decoration: line-through;
}

.chart-legend__dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
}

.chart-legend__label {
  white-space: nowrap;
}

.chart-legend__more {
  border-color: var(--ui-border, #dcdfe6);
  color: var(--ui-text-2, #606266);
}

.chart-legend__hidden-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 8px;
  max-height: 240px;
  overflow-y: auto;
}

.chart-legend__hidden-list .chart-legend__item.is-hidden {
  opacity: 1;
}

.chart-legend__hidden-list .chart-legend__item.is-hidden .chart-legend__label {
  text-decoration: none;
}

.kpi-wrap {
  flex: 1 1 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  align-content: center;
  gap: 24px 48px;
}

.kpi-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-align: center;
  min-width: 120px;
}

.kpi-value {
  font-size: 42px;
  font-weight: 700;
  line-height: 1.1;
  color: var(--ui-text-1, #303133);
  letter-spacing: -0.5px;
}

.kpi-label {
  font-size: 13px;
  color: var(--ui-text-3, #909399);
}

/* Дельта к сравнительному периоду под значением KPI */
.kpi-delta {
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: var(--ui-bg-soft, #f5f7fa);
  color: var(--ui-text-2, #606266);
}

.kpi-delta--up {
  background: rgba(45, 206, 137, 0.12);
  color: #1f9d6b;
}

.kpi-delta--down {
  background: rgba(245, 108, 108, 0.12);
  color: #d95757;
}

.kpi-delta-period {
  font-size: 11px;
  font-weight: 500;
  opacity: 0.75;
}

.chart-empty-state {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--ui-text-4, #c0c4cc);
}

.chart-empty-icon {
  font-size: 22px;
  opacity: 0.5;
}

.chart-empty-text {
  font-size: 12px;
  font-weight: 500;
}
</style>
